/**
 * Рисунки для развёртки: техника и свет раздела «Мебель» — мониторы, телевизор, приставки,
 * акустика, проигрыватели, торшеры, мелкая кухонная техника.
 *
 * Те же правила, что у остальной мебели (`elevationSymbols`): контур в миллиметрах типового
 * предмета, низ — пол (или столешница, на которой предмет стоит), сбоку перед смотрит вправо.
 */
import { UIKit, type ElevationSymbols, type SymbolComponent } from "@/shared/ui";
import { Legs, handle, line, thin } from "../../elevationParts";

/** Монитор: экран в рамке на ножке с подставкой */
const MonitorFront: SymbolComponent = (props) => (
	<UIKit.SymbolFrame viewBox="0 0 615 470" {...props}>
		<rect x="270" y="340" width="75" height="105" {...thin} />
		<rect x="170" y="440" width="275" height="25" rx="8" {...line} />
		<rect x="8" y="8" width="599" height="345" rx="10" {...line} />
		<rect x="30" y="30" width="555" height="300" {...thin} />
	</UIKit.SymbolFrame>
);

/** Монитор сбоку: тонкая панель на ножке, подставка вперёд */
const MonitorSide: SymbolComponent = (props) => (
	<UIKit.SymbolFrame viewBox="0 0 200 470" {...props}>
		<rect x="70" y="340" width="40" height="105" {...thin} />
		<rect x="10" y="440" width="180" height="25" rx="8" {...line} />
		<rect x="60" y="8" width="45" height="345" rx="10" {...line} />
	</UIKit.SymbolFrame>
);

/** Открытый ноутбук: экран и корпус с клавиатурой */
const LaptopFront: SymbolComponent = (props) => (
	<UIKit.SymbolFrame viewBox="0 0 320 220" {...props}>
		<rect x="20" y="8" width="280" height="190" rx="8" {...line} />
		<rect x="36" y="22" width="248" height="160" {...thin} />
		<rect x="4" y="196" width="312" height="20" rx="6" {...line} />
	</UIKit.SymbolFrame>
);

/** Ноутбук сбоку: откинутая крышка и плоский корпус */
const LaptopSide: SymbolComponent = (props) => (
	<UIKit.SymbolFrame viewBox="0 0 280 220" {...props}>
		<path d="M20 200L60 10" {...line} strokeLinecap="round" />
		<rect x="10" y="196" width="260" height="20" rx="6" {...line} />
	</UIKit.SymbolFrame>
);

/** Телевизор на ножках: экран в рамке */
const TvFront: SymbolComponent = (props) => (
	<UIKit.SymbolFrame viewBox="0 0 1200 700" {...props}>
		<path d="M220 640L180 695H320L290 640M980 640L1020 695H880L910 640" {...thin} />
		<rect x="8" y="8" width="1184" height="640" rx="10" {...line} />
		<rect x="30" y="30" width="1140" height="590" {...thin} />
	</UIKit.SymbolFrame>
);

/** Приставка: плоский корпус, щель дисковода и кнопка */
const ConsoleFront: SymbolComponent = (props) => (
	<UIKit.SymbolFrame viewBox="0 0 390 100" {...props}>
		<rect x="6" y="6" width="378" height="88" rx="14" {...line} />
		<path d="M40 50H250" {...thin} />
		<circle cx="330" cy="50" r="14" {...thin} />
	</UIKit.SymbolFrame>
);

/** Системный блок: башня, решётка спереди и кнопка питания */
const PcFront: SymbolComponent = (props) => (
	<UIKit.SymbolFrame viewBox="0 0 230 500" {...props}>
		<rect x="8" y="8" width="214" height="472" rx="10" {...line} />
		<circle cx="115" cy="60" r="18" {...thin} />
		{[140, 200, 260, 320, 380].map((y) => (
			<path key={y} d={`M45 ${y}H185`} {...thin} />
		))}
		<rect x="20" y="480" width="40" height="18" {...thin} />
		<rect x="170" y="480" width="40" height="18" {...thin} />
	</UIKit.SymbolFrame>
);

/** Подставка под приставку: вертикальная приставка в держателе */
const ConsoleStandFront: SymbolComponent = (props) => (
	<UIKit.SymbolFrame viewBox="0 0 440 400" {...props}>
		<rect x="150" y="10" width="140" height="350" rx="20" {...line} />
		<path d="M175 60V300" {...thin} />
		<rect x="10" y="355" width="420" height="40" rx="10" {...line} />
	</UIKit.SymbolFrame>
);

/** Напольная колонка: корпус, пищалка сверху и два круглых динамика */
const SpeakerFront: SymbolComponent = (props) => (
	<UIKit.SymbolFrame viewBox="0 0 375 1100" {...props}>
		<rect x="10" y="10" width="355" height="1040" rx="14" {...line} />
		<circle cx="187" cy="140" r="55" {...thin} />
		<circle cx="187" cy="140" r="20" {...thin} />
		<circle cx="187" cy="420" r="130" {...line} />
		<circle cx="187" cy="420" r="45" {...thin} />
		<circle cx="187" cy="760" r="130" {...line} />
		<circle cx="187" cy="760" r="45" {...thin} />
		<Legs left={30} right={345} top={1050} floor={1100} width={50} />
	</UIKit.SymbolFrame>
);

/** Проигрыватель винила: откинутая крышка, диск на корпусе, ручки спереди */
const TurntableFront: SymbolComponent = (props) => (
	<UIKit.SymbolFrame viewBox="0 0 450 390" {...props}>
		<rect x="20" y="10" width="410" height="200" rx="8" {...thin} strokeDasharray="20 14" />
		<rect x="60" y="205" width="300" height="22" rx="6" {...line} />
		<path d="M390 227V180L330 150" {...thin} strokeLinecap="round" />
		<rect x="8" y="227" width="434" height="140" rx="12" {...line} />
		<circle cx="60" cy="300" r="18" {...thin} />
		<circle cx="390" cy="300" r="18" {...thin} />
		<Legs left={30} right={420} top={367} floor={390} width={45} />
	</UIKit.SymbolFrame>
);

/** Тумба с проигрывателем: проигрыватель сверху, ниже ячейки с пластинками, ножки */
const TurntableCabinetFront: SymbolComponent = (props) => (
	<UIKit.SymbolFrame viewBox="0 0 1000 800" {...props}>
		<rect x="300" y="140" width="400" height="22" rx="6" {...line} />
		<rect x="260" y="162" width="480" height="110" rx="10" {...line} />
		<rect x="10" y="272" width="980" height="420" {...line} />
		<path d="M500 272V692" {...line} />
		{[60, 100, 140, 180, 220, 560, 600, 640].map((x) => (
			<path key={x} d={`M${x} 330V680`} {...thin} />
		))}
		<Legs left={40} right={960} top={692} floor={800} width={50} />
	</UIKit.SymbolFrame>
);

/** Саундбар: вытянутый корпус с решёткой */
const SoundbarFront: SymbolComponent = (props) => (
	<UIKit.SymbolFrame viewBox="0 0 900 80" {...props}>
		<rect x="6" y="6" width="888" height="68" rx="30" {...line} />
		<path d="M60 40H840" {...thin} strokeDasharray="8 14" />
	</UIKit.SymbolFrame>
);

/** Торшер: абажур, стойка и круглое основание */
const FloorLampFront: SymbolComponent = (props) => (
	<UIKit.SymbolFrame viewBox="0 0 700 1600" {...props}>
		<rect x="340" y="400" width="20" height="1160" {...thin} />
		<rect x="200" y="1560" width="300" height="35" rx="15" {...line} />
		<path d="M220 20H480L600 420H100Z" {...line} strokeLinejoin="round" />
	</UIKit.SymbolFrame>
);

/** Торшер на треноге: абажур на трёх ножках */
const TripodLampFront: SymbolComponent = (props) => (
	<UIKit.SymbolFrame viewBox="0 0 610 1600" {...props}>
		<path d="M305 560L40 1595M305 560L570 1595M305 560V1595" {...line} />
		<path d="M140 30H470L560 560H50Z" {...line} strokeLinejoin="round" />
	</UIKit.SymbolFrame>
);

/** Торшер-дуга: тяжёлое основание слева, дуга и купол плафона справа */
const ArcLampFront: SymbolComponent = (props) => (
	<UIKit.SymbolFrame viewBox="0 0 1480 1800" {...props}>
		<path d="M250 1740V700Q250 60 1300 300" {...line} />
		<rect x="100" y="1730" width="300" height="65" rx="10" {...line} />
		<path d="M1130 520Q1150 300 1300 300Q1450 300 1470 520Z" {...line} />
	</UIKit.SymbolFrame>
);

/** Кофемашина: корпус, носик и чашка */
const CoffeeMachineFront: SymbolComponent = (props) => (
	<UIKit.SymbolFrame viewBox="0 0 260 370" {...props}>
		<rect x="10" y="10" width="240" height="350" rx="16" {...line} />
		<rect x="40" y="130" width="180" height="200" {...thin} />
		<path d="M120 130V170" {...handle} />
		<path d="M100 270H160V320H100Z" {...thin} />
	</UIKit.SymbolFrame>
);

/** Микроволновка: дверца со стеклом слева, панель кнопок справа */
const MicrowaveFront: SymbolComponent = (props) => (
	<UIKit.SymbolFrame viewBox="0 0 500 300" {...props}>
		<rect x="8" y="8" width="484" height="270" rx="12" {...line} />
		<rect x="30" y="30" width="320" height="226" rx="8" {...thin} />
		<path d="M370 20V266" {...thin} />
		<circle cx="430" cy="90" r="22" {...thin} />
		<circle cx="430" cy="170" r="22" {...thin} />
		<rect x="20" y="278" width="40" height="18" {...thin} />
		<rect x="440" y="278" width="40" height="18" {...thin} />
	</UIKit.SymbolFrame>
);

/** Чайник и тостер рядом */
const KettleToasterFront: SymbolComponent = (props) => (
	<UIKit.SymbolFrame viewBox="0 0 450 280" {...props}>
		<path d="M30 270L50 60H170L190 270Z" {...line} strokeLinejoin="round" />
		<path d="M170 90Q230 110 190 210" {...thin} />
		<path d="M50 60L20 30" {...thin} strokeLinecap="round" />
		<rect x="240" y="120" width="200" height="150" rx="30" {...line} />
		<path d="M290 120V100M390 120V100" {...thin} />
	</UIKit.SymbolFrame>
);

/** Мусорное ведро с крышкой и педалью */
const TrashBinFront: SymbolComponent = (props) => (
	<UIKit.SymbolFrame viewBox="0 0 400 650" {...props}>
		<path d="M40 90H360L340 640H60Z" {...line} strokeLinejoin="round" />
		<rect x="25" y="20" width="350" height="70" rx="30" {...line} />
		<path d="M160 620H240" {...handle} />
	</UIKit.SymbolFrame>
);

/** Очиститель воздуха: высокий корпус, решётка сверху */
const AirPurifierFront: SymbolComponent = (props) => (
	<UIKit.SymbolFrame viewBox="0 0 350 600" {...props}>
		<rect x="10" y="10" width="330" height="580" rx="60" {...line} />
		{[70, 110, 150, 190].map((y) => (
			<path key={y} d={`M60 ${y}H290`} {...thin} />
		))}
		<circle cx="175" cy="300" r="20" {...thin} />
	</UIKit.SymbolFrame>
);

/** Робот-пылесос: плоский диск на колёсах */
const RobotVacuumFront: SymbolComponent = (props) => (
	<UIKit.SymbolFrame viewBox="0 0 350 95" {...props}>
		<rect x="5" y="15" width="340" height="70" rx="30" {...line} />
		<rect x="140" y="5" width="70" height="14" rx="6" {...thin} />
	</UIKit.SymbolFrame>
);

/** Пуф: мягкий цилиндр с пуговицей */
const PoufFront: SymbolComponent = (props) => (
	<UIKit.SymbolFrame viewBox="0 0 450 420" {...props}>
		<rect x="10" y="10" width="430" height="400" rx="70" {...line} />
		<path d="M30 80H420" {...thin} strokeDasharray="20 14" />
	</UIKit.SymbolFrame>
);

/** Кресло-мешок: бесформенная подушка */
const BeanbagFront: SymbolComponent = (props) => (
	<UIKit.SymbolFrame viewBox="0 0 800 830" {...props}>
		<path
			d="M120 820Q10 800 30 600Q60 260 260 120Q400 20 560 140Q760 320 780 620Q790 810 680 820Z"
			{...line}
		/>
		<path d="M200 560Q400 470 600 560" {...thin} />
	</UIKit.SymbolFrame>
);

/** Кресло-папасан: круглая чаша на подставке */
const PapasanFront: SymbolComponent = (props) => (
	<UIKit.SymbolFrame viewBox="0 0 930 560" {...props}>
		<path d="M300 560Q300 420 465 420Q630 420 630 560Z" {...line} />
		<path d="M10 120Q30 440 465 440Q900 440 920 120Z" {...line} />
		<path d="M80 150Q465 260 850 150" {...thin} />
	</UIKit.SymbolFrame>
);

export const FURNITURE_TECH_ELEVATIONS: Record<string, ElevationSymbols> = {
	monitor: { front: MonitorFront, side: MonitorSide },
	laptop: { front: LaptopFront, side: LaptopSide },
	tv: { front: TvFront },
	playstation: { front: ConsoleFront },
	"gaming-pc": { front: PcFront },
	"console-stand": { front: ConsoleStandFront },
	"speaker-floor": { front: SpeakerFront },
	turntable: { front: TurntableFront },
	"turntable-cabinet": { front: TurntableCabinetFront },
	soundbar: { front: SoundbarFront },
	"floor-lamp": { front: FloorLampFront, side: FloorLampFront },
	"floor-lamp-tripod": { front: TripodLampFront, side: TripodLampFront },
	"floor-lamp-arc": { front: ArcLampFront, side: FloorLampFront },
	"floor-lamp-gold": { front: FloorLampFront, side: FloorLampFront },
	"coffee-machine": { front: CoffeeMachineFront },
	microwave: { front: MicrowaveFront },
	"kettle-toaster": { front: KettleToasterFront },
	"trash-bin": { front: TrashBinFront, side: TrashBinFront },
	"air-purifier": { front: AirPurifierFront, side: AirPurifierFront },
	"robot-vacuum": { front: RobotVacuumFront, side: RobotVacuumFront },
	pouf: { front: PoufFront, side: PoufFront },
	"stool-kids": { front: PoufFront, side: PoufFront },
	beanbag: { front: BeanbagFront, side: BeanbagFront },
	"armchair-papasan": { front: PapasanFront, side: PapasanFront },
};
